import { useEffect, useRef } from 'react';
import { Badge } from '../common/Badge';
import type { BadgeVariant } from '../common/Badge';
import { useToast } from '../../contexts/ToastContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type { LogLevel, SystemLog } from '../../types';
import { formatTimestamp } from '../../utils/dateFormat';

export interface LogDetailDialogProps {
  open: boolean;
  log: SystemLog | null;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  position?: number;
  total?: number;
}

function getLevelBadgeVariant(level: LogLevel): 'neutral' | 'info' | 'warning' | 'error' {
  if (level === 'Information') return 'info';
  if (level === 'Warning') return 'warning';
  if (level === 'Error' || level === 'Critical') return 'error';
  return 'neutral';
}

function getLevelMeta(level: LogLevel): { badgeVariant: BadgeVariant; tone: string; shortLabel: string } {
  if (level === 'Critical') {
    return {
      badgeVariant: 'purple',
      tone:
        'text-purple-700 bg-purple-100 ring-1 ring-inset ring-purple-300 dark:text-purple-300 dark:bg-purple-950/50 dark:ring-purple-800',
      shortLabel: 'Critical',
    };
  }

  if (level === 'Error') {
    return {
      badgeVariant: 'error',
      tone:
        'text-red-700 bg-red-100 ring-1 ring-inset ring-red-300 dark:text-red-300 dark:bg-red-950/50 dark:ring-red-800',
      shortLabel: 'Error',
    };
  }

  if (level === 'Warning') {
    return {
      badgeVariant: 'warning',
      tone:
        'text-amber-700 bg-amber-100 ring-1 ring-inset ring-amber-300 dark:text-amber-300 dark:bg-amber-950/50 dark:ring-amber-800',
      shortLabel: 'Warning',
    };
  }

  if (level === 'Information') {
    return {
      badgeVariant: 'success',
      tone:
        'text-emerald-700 bg-emerald-100 ring-1 ring-inset ring-emerald-300 dark:text-emerald-300 dark:bg-emerald-950/50 dark:ring-emerald-800',
      shortLabel: 'Info',
    };
  }

  if (level === 'Debug') {
    return {
      badgeVariant: 'info',
      tone:
        'text-sky-700 bg-sky-100 ring-1 ring-inset ring-sky-300 dark:text-sky-300 dark:bg-sky-950/50 dark:ring-sky-800',
      shortLabel: 'Debug',
    };
  }

  return {
    badgeVariant: 'neutral',
    tone:
      'text-gray-700 bg-gray-100 ring-1 ring-inset ring-gray-300 dark:text-gray-300 dark:bg-gray-900/70 dark:ring-gray-700',
    shortLabel: 'Trace',
  };
}

function LogLevelIcon({ level, className = 'h-4 w-4' }: { level: LogLevel; className?: string }) {
  if (level === 'Error' || level === 'Critical') {
    return (
      <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M10 1.75a8.25 8.25 0 1 0 0 16.5 8.25 8.25 0 0 0 0-16.5Zm-.75 4a.75.75 0 0 1 1.5 0v5a.75.75 0 0 1-1.5 0v-5Zm.75 8.75a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  if (level === 'Warning') {
    return (
      <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M8.553 3.112a1.75 1.75 0 0 1 2.894 0l6.149 10.143A1.75 1.75 0 0 1 16.15 16H3.85a1.75 1.75 0 0 1-1.446-2.745L8.553 3.112ZM10 7.25a.75.75 0 0 0-.75.75v2.75a.75.75 0 0 0 1.5 0V8A.75.75 0 0 0 10 7.25Zm0 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  if (level === 'Information') {
    return (
      <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M10 1.75a8.25 8.25 0 1 0 0 16.5 8.25 8.25 0 0 0 0-16.5ZM9.25 8a.75.75 0 0 1 1.5 0v5a.75.75 0 0 1-1.5 0V8Zm1.5-2.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M10 1.75A8.25 8.25 0 1 0 10 18.25 8.25 8.25 0 0 0 10 1.75ZM5.75 10a.75.75 0 0 1 .75-.75h6.69l-2.22-2.22a.75.75 0 0 1 1.06-1.06l3.5 3.5a.75.75 0 0 1 0 1.06l-3.5 3.5a.75.75 0 1 1-1.06-1.06l2.22-2.22H6.5A.75.75 0 0 1 5.75 10Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function DetailCard({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-1 break-all text-sm text-gray-900 dark:text-gray-100 ${mono ? 'font-mono text-xs' : ''}`}>
        {value || '—'}
      </p>
    </div>
  );
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall back below.
    }
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
  } catch {
    return false;
  }
}

export function LogDetailDialog({
  open,
  log,
  onClose,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  position = 0,
  total = 0,
}: LogDetailDialogProps) {
  const { addToast } = useToast();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const levelMeta = log ? getLevelMeta(log.level) : getLevelMeta('Information');

  useEscapeKey(onClose, open);

  useEffect(() => {
    if (!open) return;

    previousActiveElement.current = document.activeElement as HTMLElement;
    closeButtonRef.current?.focus();
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      previousActiveElement.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleArrowNavigation = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        event.preventDefault();
        onPrevious();
      }
      if (event.key === 'ArrowRight' && hasNext && onNext) {
        event.preventDefault();
        onNext();
      }
    };

    document.addEventListener('keydown', handleArrowNavigation);
    return () => document.removeEventListener('keydown', handleArrowNavigation);
  }, [hasNext, hasPrevious, onNext, onPrevious, open]);

  const prettyProperties = log?.properties ? JSON.stringify(log.properties, null, 2) : '—';

  const handleCopy = async () => {
    if (!log) return;

    const didCopy = await copyToClipboard(JSON.stringify(log, null, 2));
    if (!didCopy) {
      addToast('Unable to copy log JSON.', 'error');
      return;
    }

    addToast('Log entry copied as JSON.', 'success');
  };

  if (!open || !log) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="log-detail-title"
        aria-describedby="log-detail-description"
        className="relative mx-4 max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="border-b border-gray-200 bg-gradient-to-r from-slate-900 via-slate-800 to-primary-900 px-6 py-4 text-white dark:border-gray-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium ring-1 ring-inset ring-white/20">
                <LogLevelIcon level={log.level} />
                Severity
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 id="log-detail-title" className="text-xl font-semibold tracking-tight">
                  Log Details
                </h2>
                <Badge variant={levelMeta.badgeVariant} size="sm">
                  <span className="inline-flex items-center gap-1.5">
                    <LogLevelIcon level={log.level} className="h-3.5 w-3.5" />
                    <span>{levelMeta.shortLabel}</span>
                  </span>
                </Badge>
              </div>
              <p id="log-detail-description" className="text-sm text-white/80">
                {formatTimestamp(log.timestamp)}
              </p>
            </div>
            <div className="rounded-md bg-white/10 px-3 py-2 text-xs font-medium ring-1 ring-inset ring-white/20">
              {position > 0 && total > 0 ? `${position} of ${total}` : 'Position unavailable'}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-6 py-3 dark:border-gray-700 dark:bg-gray-900/70">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              aria-label="Previous log"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M11.78 4.22a.75.75 0 0 1 0 1.06L7.06 10l4.72 4.72a.75.75 0 1 1-1.06 1.06l-5.25-5.25a.75.75 0 0 1 0-1.06l5.25-5.25a.75.75 0 0 1 1.06 0Z"
                  clipRule="evenodd"
                />
              </svg>
              Previous
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!hasNext}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              aria-label="Next log"
            >
              Next
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M8.22 4.22a.75.75 0 0 1 1.06 0l5.25 5.25a.75.75 0 0 1 0 1.06l-5.25 5.25a.75.75 0 1 1-1.06-1.06L12.94 10 8.22 5.28a.75.75 0 0 1 0-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-200 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>

        <div className="max-h-[calc(90vh-10rem)] space-y-5 overflow-y-auto bg-gray-50 px-6 py-5 dark:bg-gray-900/50">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <DetailCard label="Log ID" value={log.id} mono />
            <DetailCard label="Timestamp" value={formatTimestamp(log.timestamp)} />
            <DetailCard label="Logger" value={log.logger} mono />
            <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Level
              </p>
              <div className="mt-1 inline-flex items-center gap-2">
                <Badge variant={getLevelBadgeVariant(log.level)} size="sm">
                  <span className="inline-flex items-center gap-1.5">
                    <LogLevelIcon level={log.level} className="h-3.5 w-3.5" />
                    <span>{levelMeta.shortLabel}</span>
                  </span>
                </Badge>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${levelMeta.tone}`}>
                  {log.level}
                </span>
              </div>
            </div>
            <DetailCard label="Correlation ID" value={log.correlationId ?? '—'} mono />
            <DetailCard label="User ID" value={log.userId ?? '—'} mono />
            <DetailCard label="Tenant ID" value={log.tenantId ?? '—'} mono />
            <div className="md:col-span-2 lg:col-span-4">
              <DetailCard label="Request Path" value={log.requestPath ?? '—'} mono />
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">Message</p>
            <pre className="mt-2 whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
              {log.message}
            </pre>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Exception
            </p>
            <pre className="mt-2 max-h-52 overflow-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
              {log.exception ?? '—'}
            </pre>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              Properties (JSON)
            </p>
            <pre className="mt-2 max-h-72 overflow-auto rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
              {prettyProperties}
            </pre>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-800 focus:ring-2 focus:ring-primary-500 focus:outline-none"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M5.5 3A2.5 2.5 0 0 0 3 5.5v9A2.5 2.5 0 0 0 5.5 17h6A2.5 2.5 0 0 0 14 14.5v-9A2.5 2.5 0 0 0 11.5 3h-6Zm1.75 2a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5h-2.5ZM16 6.25a.75.75 0 0 0-1.5 0v8.25c0 .69-.56 1.25-1.25 1.25H8.5a.75.75 0 0 0 0 1.5h4.75A2.75 2.75 0 0 0 16 14.5V6.25Z"
                clipRule="evenodd"
              />
            </svg>
            Copy JSON
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-200 focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
